import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ZodType } from "zod";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { ListPanel } from "@/components/ListPanel";
import { useSavedViews } from "@/hooks/useSavedViews";

interface SavedViewsProps<T> {
    storageKey: string;
    schema: ZodType<T>;
    value: T;
    defaultValue?: T;
    onApply: (value: T) => void;
    configuration?: ReactNode;
    views?: boolean;
    config?: boolean;
}

export const SavedViews = <T,>({
    storageKey,
    schema,
    value,
    defaultValue,
    onApply,
    configuration,
    views = true,
    config = true,
}: SavedViewsProps<T>) => {
    const { t } = useTranslation("global");
    const model = useSavedViews(
        storageKey,
        schema,
        value,
        defaultValue ?? value,
        onApply
    );
    const changesId = useId();
    const changes = model.changes
        .map((change) => t(`workspace.list.changes.${change}`))
        .join(", ");
    const changeHint = model.modified
        ? t(
              model.selected
                  ? "workspace.list.saved_changes_hint"
                  : "workspace.list.initial_changes_hint",
              { name: model.selected?.name, changes }
          )
        : t("workspace.list.no_changes_hint");
    const [panel, setPanel] = useState<"save" | "columns" | null>(null);
    const [name, setName] = useState("");
    const [deleting, setDeleting] = useState(false);
    const creating =
        !model.selected ||
        name.trim().toLocaleLowerCase() !==
            model.selected.name.toLocaleLowerCase();
    const duplicate =
        Boolean(name.trim()) &&
        (name.trim().toLocaleLowerCase() ===
            t("workspace.list.default_view").toLocaleLowerCase() ||
            model.views.some(
                (view) =>
                    view.id !== (creating ? undefined : model.selected?.id) &&
                    view.name.toLocaleLowerCase() ===
                        name.trim().toLocaleLowerCase()
            ));
    const canSave =
        model.valid &&
        Boolean(name.trim()) &&
        !duplicate &&
        (!creating || !model.full);
    const save = (): void => {
        if (canSave && model.save(name, creating ? "create" : "update")) {
            setPanel(null);
        }
    };
    const close = (): void => {
        setPanel(null);
        setDeleting(false);
    };
    return (
        <div className="flex w-full max-w-full items-center gap-1 sm:w-auto">
            {views && (
                <Select<string>
                    value={model.selected?.id || "__default_view__"}
                    ariaLabel={t("workspace.list.views")}
                    size="md"
                    className="w-full min-w-0 flex-1 sm:!w-40 sm:flex-none"
                    options={[
                        {
                            value: "__default_view__",
                            label: t("workspace.list.default_view"),
                        },
                        ...model.views.map((view) => ({
                            value: view.id,
                            label: view.name,
                        })),
                    ]}
                    onChange={(id) => {
                        model.apply(id);
                        close();
                    }}
                />
            )}
            <span id={changesId} className="sr-only" role="status">
                {changeHint}
            </span>
            {views && (
                <Button
                    variant="ghost"
                    size="lg"
                    icon="save"
                    iconStrokeWidth={1.5}
                    onlyicon
                    title={`${t("workspace.list.save_view")}. ${changeHint}`}
                    aria-label={t("workspace.list.save_view")}
                    aria-describedby={changesId}
                    aria-expanded={panel === "save"}
                    className={`mx-1 shrink-0 ${model.modified ? "!text-primary dark:!text-primary-300" : "!text-slate-600 dark:!text-zinc-300"}`}
                    onClick={() => {
                        setName(model.selected?.name ?? "");
                        setDeleting(false);
                        setPanel(panel === "save" ? null : "save");
                    }}
                />
            )}
            {configuration && config && (
                <Button
                    variant="ghost"
                    size="lg"
                    icon="columns3"
                    iconStrokeWidth={1.5}
                    onlyicon
                    title={t("workspace.list.configure")}
                    aria-label={t("workspace.list.configure")}
                    aria-expanded={panel === "columns"}
                    className="shrink-0 !text-slate-600 dark:!text-zinc-300"
                    onClick={() => {
                        setDeleting(false);
                        setPanel(panel === "columns" ? null : "columns");
                    }}
                />
            )}
            <ListPanel
                isOpen={panel === "columns"}
                onClose={close}
                title={t("workspace.list.configure")}
            >
                {configuration}
            </ListPanel>
            <ListPanel
                isOpen={panel === "save"}
                onClose={close}
                title={t("workspace.list.save_view")}
            >
                {model.modified && (
                    <p className="mb-3 text-xs text-slate-600 dark:text-zinc-300">
                        {changeHint}
                    </p>
                )}
                {deleting ? (
                    <div className="flex flex-wrap items-center gap-1">
                        <p className="text-sm">
                            {t("workspace.list.delete_view_confirm", {
                                name: model.selected?.name,
                            })}
                        </p>
                        <Button
                            size="xl"
                            variant="secondary"
                            autoFocus
                            onClick={() => setDeleting(false)}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            size="xl"
                            variant="danger"
                            onClick={() => {
                                model.remove();
                                close();
                            }}
                        >
                            {t("common.delete")}
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-end gap-1">
                        <div className="w-72 max-w-full">
                            <Input
                                label={t("workspace.list.view_name")}
                                value={name}
                                onChange={(event) =>
                                    setName(event.target.value)
                                }
                                maxLength={60}
                                error={
                                    duplicate
                                        ? t("workspace.list.duplicate_view")
                                        : undefined
                                }
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        save();
                                    }
                                }}
                            />
                        </div>
                        <Button
                            size="xl"
                            icon="save"
                            className="!h-[38px]"
                            onClick={save}
                            disabled={!canSave}
                        >
                            {t(
                                creating
                                    ? "workspace.list.save_new_view"
                                    : "workspace.list.update_view"
                            )}
                        </Button>
                        {model.selected && (
                            <Button
                                variant="ghost"
                                size="xl"
                                icon="trash"
                                onClick={() => setDeleting(true)}
                            >
                                {t("workspace.list.delete_view")}
                            </Button>
                        )}
                        <p className="basis-full text-xs text-slate-600 dark:text-zinc-300">
                            {t(
                                model.full && creating
                                    ? "workspace.list.view_limit"
                                    : "workspace.list.inline_save_hint"
                            )}
                        </p>
                    </div>
                )}
            </ListPanel>
        </div>
    );
};
